import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { message } from "antd";

import firebaseService, {
  getNotificationPermission,
  isWebView,
} from "../services/firebase";
import deviceService from "../services/deviceService";
import { isDevelopment } from "src/module/helpers";
import { firebaseLogoutDevice } from "src/store/actions/deviceAction";

const NotificationContext = createContext({});

export const useNotification = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
  const [permission, setPermission] = useState("default");
  const [fcmToken, setFcmToken] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [deviceStatus, setDeviceStatus] = useState({});

  const user = useSelector((state) => state?.authentication ?? {});
  const appSetting = useSelector((state) => state?.app_setting?.response);
  const Profile = useSelector((state) => state?.Profile?.response?.full);
  const dispatch = useDispatch();

  const isAuthenticated = Boolean(user?.isAuthenticated);
  const currentToken = user?.deviceStatus?.firebaseState?.token ?? null;
  const userId = Profile?.id;

  const permissionRef = useRef(permission);
  const initializedRef = useRef(false);
  const initializingRef = useRef(false);
  const listenersRegisteredRef = useRef(false);
  const registeringUserRef = useRef(null);
  const registeringRef = useRef(false);
  const lastRegisteredKeyRef = useRef(null);

  const isTeknisi = [5];
  const isReseller = [3];

  useEffect(() => {
    permissionRef.current = permission;
  }, [permission]);

  const handleNewMessage = useCallback((payload) => {
    if (isDevelopment) {
      console.log("[NOTIFICATION] New message:", payload);
    }

    const newNotification = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      title: payload?.notification?.title || "Notification",
      body: payload?.notification?.body || "",
      data: payload?.data || {},
      timestamp: new Date().toISOString(),
      read: false,
      type: payload?.data?.type || "general",
    };

    setNotifications((previous) => [newNotification, ...previous].slice(0, 50));
    setUnreadCount((previous) => previous + 1);

    if (payload?.data?.sound !== "false") {
      firebaseService.playNotificationSound();
    }

    if (
      payload?.data?.show_toast !== "false" &&
      typeof window !== "undefined"
    ) {
      window.dispatchEvent(
        new CustomEvent("show-toast", { detail: newNotification }),
      );
    }
  }, []);

  const handlePermissionGranted = useCallback(() => {
    permissionRef.current = "granted";
    setPermission("granted");
    if (isDevelopment) {
      console.log("[NOTIFICATION] Permission granted");
    }
  }, []);

  const handlePermissionDenied = useCallback(() => {
    permissionRef.current = "denied";
    setPermission("denied");

    message.warning({
      content: (
        <span>
          Notifikasi diblokir. Silakan aktifkan secara manual melalui{" "}
          <b>
            pengaturan browser (ikon 🔒 di address bar → Notifications → Allow)
          </b>
          .
        </span>
      ),
      duration: 6,
    });

    if (isDevelopment) {
      console.log("[NOTIFICATION] Permission denied");
    }
  }, []);

  // Inisialisasi hanya sekali; listener tidak didaftarkan berulang kali.
  const initializeFirebase = useCallback(async () => {
    if (initializedRef.current) {
      return true;
    }

    if (initializingRef.current) {
      return false;
    }

    initializingRef.current = true;

    try {
      const initialized = await firebaseService.initialize();
      if (!initialized) {
        return false;
      }

      if (!listenersRegisteredRef.current) {
        firebaseService.setupForegroundListener();
        firebaseService.addListener("message", handleNewMessage);
        firebaseService.addListener(
          "permission_granted",
          handlePermissionGranted,
        );
        firebaseService.addListener(
          "permission_denied",
          handlePermissionDenied,
        );
        listenersRegisteredRef.current = true;
      }

      const state = firebaseService.getState?.() ?? {};
      const nextPermission = state.permission || getNotificationPermission();
      permissionRef.current = nextPermission;
      setPermission(nextPermission);
      setFcmToken(state.token || null);
      initializedRef.current = true;
      setIsInitialized(true);

      if (isDevelopment) {
        console.log("[NOTIFICATION] Firebase initialized");
      }

      return true;
    } catch (error) {
      if (isDevelopment) {
        console.error("[NOTIFICATION] Initialize failed:", error);
      }
      return false;
    } finally {
      initializingRef.current = false;
    }
  }, [handleNewMessage, handlePermissionGranted, handlePermissionDenied]);

  // Satu pintu untuk memulai saga registrasi perangkat.
  // Deduplikasi per user, bukan satu lock global untuk semua akun.
  const safeRegisterDevice = useCallback(
    (targetUserId, options = {}) => {
      if (!targetUserId || !isAuthenticated) {
        return false;
      }

      const targetKey = String(targetUserId);
      const tokenForKey =
        options.token ||
        fcmToken ||
        (typeof window !== "undefined"
          ? localStorage.getItem(isWebView ? "fcm_token_android" : "fcm_token")
          : null) ||
        "pending-token";
      const registrationKey = `${targetKey}:${tokenForKey}`;
      const force = Boolean(options.force);

      if (
        registeringRef.current &&
        registeringUserRef.current === registrationKey
      ) {
        if (isDevelopment) {
          console.log(
            "[NOTIFICATION] Registration already in progress:",
            targetKey,
          );
        }
        return false;
      }

      if (!force && lastRegisteredKeyRef.current === registrationKey) {
        if (isDevelopment) {
          console.log(
            "[NOTIFICATION] Registration already requested:",
            registrationKey,
          );
        }
        return false;
      }

      registeringRef.current = true;
      registeringUserRef.current = registrationKey;
      lastRegisteredKeyRef.current = registrationKey;

      if (isDevelopment) {
        console.log("[NOTIFICATION] DEVICE_INIT_REQUEST:", targetKey);
      }

      dispatch({
        type: "DEVICE_INIT_REQUEST",
        payload: targetUserId,
      });

      // Redux-Saga berjalan di luar callback ini. Lepaskan lock in-flight
      // pada microtask berikutnya; lastRegisteredKeyRef tetap mencegah
      // dispatch duplikat untuk user yang sama.
      Promise.resolve().then(() => {
        if (registeringUserRef.current === registrationKey) {
          registeringRef.current = false;
          registeringUserRef.current = null;
        }
      });

      return true;
    },
    [dispatch, isAuthenticated, fcmToken],
  );

  const handlePermissionChange = useCallback(
    async (nextPermission) => {
      if (!nextPermission || nextPermission === permissionRef.current) {
        return;
      }

      permissionRef.current = nextPermission;
      setPermission(nextPermission);

      if (isDevelopment) {
        console.log("[NOTIFICATION] Permission changed:", nextPermission);
      }

      if (nextPermission === "unsupported" || nextPermission === "default") {
        return;
      }

      if (nextPermission === "denied") {
        if (isDevelopment) {
          console.log("[NOTIFICATION] User blocked notifications");
        }

        // Ini hanya menonaktifkan asosiasi push jika saga memang didesain
        // khusus untuk itu. Jangan mencabut sesi API karena izin browser.
        if (user?.deviceStatus?.session) {
          dispatch(firebaseLogoutDevice());
        }
        // Jika izin diaktifkan kembali, token yang sama pun harus dapat
        // didaftarkan ulang karena asosiasi push mungkin sudah dinonaktifkan.
        lastRegisteredKeyRef.current = null;
        return;
      }

      if (nextPermission === "granted") {
        if (isWebView || !isAuthenticated || !userId) {
          return;
        }

        const initialized = await initializeFirebase();
        if (!initialized) {
          return;
        }

        // Jangan mensyaratkan token berubah. Backend/saga harus idempoten
        // dan menghubungkan token yang ada ke akun yang sedang aktif.
        const state = firebaseService.getState?.() ?? {};
        safeRegisterDevice(userId, {
          token: state.token || fcmToken || undefined,
        });
      }
    },
    [
      dispatch,
      initializeFirebase,
      isAuthenticated,
      safeRegisterDevice,
      user?.deviceStatus?.session,
      userId,
      fcmToken,
    ],
  );

  const requestPermission = useCallback(async () => {
    const initialized = await initializeFirebase();
    if (!initialized && isDevelopment) {
      console.warn(
        "[NOTIFICATION] Firebase could not initialize before permission request",
      );
    }

    try {
      const result = await firebaseService.requestPermission();
      await handlePermissionChange(result);
      return result;
    } catch (error) {
      if (isDevelopment) {
        console.error("[NOTIFICATION] Request permission failed:", error);
      }
      return getNotificationPermission();
    }
  }, [handlePermissionChange, initializeFirebase]);

  const logoutFromDevice = useCallback(async () => {
    try {
      const result = await deviceService.logoutFromDevice();
      setDeviceStatus(deviceService.getStatus());
      setFcmToken(null);
      lastRegisteredKeyRef.current = null;
      registeringRef.current = false;
      registeringUserRef.current = null;
      return result;
    } catch (error) {
      if (isDevelopment) {
        console.error("[NOTIFICATION] Logout failed:", error);
      }
      return { success: false, error };
    }
  }, []);

  const markAsRead = useCallback((notificationId) => {
    setNotifications((previous) =>
      previous.map((notification) =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification,
      ),
    );
    setUnreadCount((previous) => Math.max(0, previous - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((previous) =>
      previous.map((notification) => ({ ...notification, read: true })),
    );
    setUnreadCount(0);
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const checkDeviceStatus = useCallback(() => {
    const status = deviceService.getStatus();
    setDeviceStatus(status);
    return status;
  }, []);

  // Pantau perubahan permission. Event digunakan jika tersedia; polling hanya fallback.
  useEffect(() => {
    if (
      appSetting?.push_notifications !== 1 ||
      !isAuthenticated ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    let disposed = false;
    let permissionStatus = null;
    let fallbackInterval = null;
    let checking = false;

    const syncPermission = async () => {
      if (disposed || checking) return;
      checking = true;
      try {
        const nextPermission = getNotificationPermission();
        if (nextPermission !== permissionRef.current) {
          await handlePermissionChange(nextPermission);
        }
      } catch (error) {
        if (isDevelopment) {
          console.warn("[NOTIFICATION] Permission check failed:", error);
        }
      } finally {
        checking = false;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void syncPermission();
      }
    };

    const onFocus = () => void syncPermission();

    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);

    const setupListener = async () => {
      if (!navigator.permissions?.query) {
        fallbackInterval = window.setInterval(
          () => void syncPermission(),
          5000,
        );
        return;
      }

      try {
        permissionStatus = await navigator.permissions.query({
          name: "notifications",
        });
        if (disposed) return;
        permissionStatus.addEventListener("change", syncPermission);
        await syncPermission();
      } catch (error) {
        if (isDevelopment) {
          console.warn(
            "[NOTIFICATION] Permission event unavailable; using fallback polling",
            error,
          );
        }
        fallbackInterval = window.setInterval(
          () => void syncPermission(),
          5000,
        );
      }
    };

    void setupListener();

    return () => {
      disposed = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      permissionStatus?.removeEventListener("change", syncPermission);
      if (fallbackInterval !== null) window.clearInterval(fallbackInterval);
    };
  }, [appSetting?.push_notifications, isAuthenticated, handlePermissionChange]);

  // Callback dari native Android WebView.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const previousHandler = window.onNotificationPermissionResult;
    window.onNotificationPermissionResult = (granted) => {
      if (granted) {
        void handlePermissionChange("granted");
      } else {
        void handlePermissionChange("denied");
      }
    };

    return () => {
      if (window.onNotificationPermissionResult) {
        window.onNotificationPermissionResult = previousHandler || null;
      }
    };
  }, [handlePermissionChange]);

  // Token dari native Android WebView. Listener dipasang hanya saat user valid.
  useEffect(() => {
    if (
      !isWebView ||
      !isAuthenticated ||
      !userId ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const registerNativeToken = (token) => {
      if (!token) return;
      localStorage.setItem("fcm_token_android", token);
      setFcmToken(token);
      setPermission("granted");
      permissionRef.current = "granted";
      setIsInitialized(true);
      initializedRef.current = true;
      safeRegisterDevice(userId, { token, force: true });
    };

    const handler = (event) => {
      const { token } = event?.detail || {};
      registerNativeToken(token);
    };

    window.addEventListener("fcm-token-ready", handler);

    // Jangan kehilangan event native yang terkirim sebelum listener terpasang.
    const existingToken = localStorage.getItem("fcm_token_android");
    if (existingToken) {
      setFcmToken(existingToken);
      safeRegisterDevice(userId, { token: existingToken });
    }

    return () => window.removeEventListener("fcm-token-ready", handler);
  }, [isAuthenticated, userId, safeRegisterDevice]);

  // Inisialisasi dan registrasi FCM Web.
  useEffect(() => {
    if (
      isWebView ||
      !isAuthenticated ||
      !userId ||
      appSetting?.user_config?.push_notifications !== 1
    ) {
      return;
    }

    const isResellerAllowed = isReseller.includes(Profile?.level_access);
    const isTeknisiAllowed =
      isTeknisi.includes(Profile?.level_access) &&
      Boolean(Profile?.permissions?.management_notifikasi);
    const isAllowedRole = isResellerAllowed || isTeknisiAllowed;

    if (!isAllowedRole) return;

    let cancelled = false;

    const initializeAndRegister = async () => {
      const currentPermission = getNotificationPermission();
      permissionRef.current = currentPermission;
      setPermission(currentPermission);

      if (currentPermission !== "granted") return;

      const initialized = await initializeFirebase();
      if (!initialized || cancelled) return;

      try {
        const result = await firebaseService.comparePrevToken(currentToken);
        if (cancelled || !result?.token) return;

        setFcmToken(result.token);

        // Registrasi tetap dijalankan untuk memastikan asosiasi akun aktif,
        // termasuk ketika token sama tetapi akun yang login telah berubah.
        safeRegisterDevice(userId, {
          token: result.token,
          force: Boolean(result.changed),
        });
      } catch (error) {
        if (isDevelopment) {
          console.error("[NOTIFICATION] FCM token check failed:", error);
        }
      }
    };

    void initializeAndRegister();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    userId,
    currentToken,
    appSetting?.user_config?.push_notifications,
    Profile?.level_access,
    Profile?.permissions?.management_notifikasi,
    initializeFirebase,
    safeRegisterDevice,
  ]);

  // Reset dedupe saat logout; akun berbeda juga diizinkan melalui key userId.
  useEffect(() => {
    if (!isAuthenticated) {
      lastRegisteredKeyRef.current = null;
      registeringRef.current = false;
      registeringUserRef.current = null;
    }
  }, [isAuthenticated]);

  // Muat notifikasi tersimpan satu kali.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem("notifications");
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed)) {
        setNotifications(parsed.slice(0, 50));
        setUnreadCount(
          parsed.filter((notification) => !notification.read).length,
        );
      }
    } catch (error) {
      if (isDevelopment) {
        console.warn(
          "[NOTIFICATION] Failed to parse saved notifications:",
          error,
        );
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(
        "notifications",
        JSON.stringify(notifications.slice(0, 50)),
      );
    }
  }, [notifications]);

  const value = {
    permission,
    fcmToken,
    notifications,
    unreadCount,
    isInitialized,
    deviceStatus,
    requestPermission,
    logoutFromDevice,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    checkDeviceStatus,
    isPermissionGranted: permission === "granted",
    isPermissionDenied: permission === "denied",
    isPermissionDefault: permission === "default",
    hasFcmToken: Boolean(fcmToken),
    isDeviceReady: Boolean(deviceStatus?.isLoggedIn && fcmToken),
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
